import React, { useState } from 'react';
import { Box, Menu, MenuItem } from '@mui/material';
import { LOCALES, LOCALE_CODE, Locale, useLocale, useT } from '../lib/i18n';
import en from '../lib/locales/en';
import uz from '../lib/locales/uz';
import ru from '../lib/locales/ru';
import { PALETTE, RADIUS } from '../theme';

/** Each language is named in itself, so it is legible to the person looking for it. */
const NAMES: Record<Locale, string> = {
  en: en.languageName,
  uz: uz.languageName,
  ru: ru.languageName,
};

/**
 * Language switch. A two-letter button rather than a full row of options: on a
 * 390px header it has to sit beside the wordmark and the CTA, and three visible
 * options do not fit without dropping something that matters more.
 */
export default function LanguageMenu() {
  const { locale, setLocale } = useLocale();
  const t = useT();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={anchor ? true : undefined}
        aria-label={`${t.languageName} — change language`}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          height: 40,
          px: 1.5,
          border: `2px solid ${PALETTE.hairline}`,
          borderRadius: `${RADIUS.control}px`,
          backgroundColor: PALETTE.ground,
          color: PALETTE.inkMuted,
          font: 'inherit',
          fontSize: 13,
          fontWeight: 800,
          letterSpacing: '0.06em',
          cursor: 'pointer',
          transition: 'color 150ms ease, border-color 150ms ease',
          '&:hover': { color: PALETTE.ink, borderColor: PALETTE.inkFaint },
        }}
      >
        {LOCALE_CODE[locale]}
        <Box
          component="svg"
          viewBox="0 0 10 6"
          aria-hidden="true"
          sx={{ width: 9, height: 6, fill: 'currentColor' }}
        >
          <path d="M0 0h10L5 6z" />
        </Box>
      </Box>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 168,
              border: `2px solid ${PALETTE.hairline}`,
              borderRadius: `${RADIUS.card}px`,
              boxShadow: `0 4px 0 0 ${PALETTE.hairline}`,
            },
          },
        }}
      >
        {LOCALES.map((option) => (
          <MenuItem
            key={option}
            selected={option === locale}
            onClick={() => {
              setLocale(option);
              setAnchor(null);
            }}
            sx={{
              gap: 1.5,
              py: 1.25,
              fontSize: 15,
              fontWeight: 700,
              color: option === locale ? PALETTE.signal : PALETTE.ink,
              '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: PALETTE.signalSoft },
            }}
          >
            <Box component="span" sx={{ fontSize: 12, fontWeight: 800, color: PALETTE.inkFaint, width: 22 }}>
              {LOCALE_CODE[option]}
            </Box>
            {NAMES[option]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
